import React, { useState, useMemo, useEffect } from 'react';
import {
  FileSpreadsheet, Shield, Euro, AlertTriangle, Activity,
  FileText, ChevronRight, BarChart3, Share2, Calendar, Mail, X
} from 'lucide-react';
import { useFormat } from '../../i18n/format.js';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import { useVehicles, useDrivers, useMaintenance, useClaims, useInsurance } from '../../index.js';
import { reportAPI, reportSubscriptionAPI, fleetInsuranceExportAPI } from '../../api.js';
import { useAuth } from '../../v3/context/AuthContext.jsx';
import { PageTemplate } from '../common/PageTemplate.jsx';
import { useTabPermissions } from '../../v3/hooks/useTabPermissions.js';
import { ReadOnlyGuard } from '../../v3/components/common/ReadOnlyGuard.jsx';

// Extracted modals (keep ReportModal and ExportModal, remove ArchiveModal)
import ReportModal from './modals/ReportModal.jsx';
import ExportModal from './modals/ExportModal.jsx';

// Annual Summary Tab
import AnnualSummaryTab from './tabs/AnnualSummaryTab.jsx';
import { CLOSED_CLAIM_STATUSES } from '../../hooks/useVehicleInsuranceData.js';

// Fleet Health Heatmap
import FleetHealthHeatmap from './FleetHealthHeatmap.jsx';

// Share and Scheduling components
import { ShareDialog } from '../shares/index.js';
import ReportScheduleWizard from './ReportScheduleWizard.jsx';

/**
 * ReportsView V3 - Tab-Based Interface
 *
 * Redesigned with 3 question-driven tabs:
 * - Conformité: "Ma flotte est-elle conforme?"
 * - Coûts: "Ma flotte coûte-t-elle trop cher?"
 * - Risques: "Où sont mes risques opérationnels?"
 *
 * Archives are accessed via dedicated sidebar module (no duplication)
 */

// Tab definitions — `label` = repli FR, l'affichage passe par t('reports.tabs.<id>')
const TABS = [
  { id: 'conformite', label: 'Conformité', icon: Shield },
  { id: 'couts', label: 'Coûts', icon: Euro },
  { id: 'risques', label: 'Risques', icon: AlertTriangle },
  { id: 'sante_flotte', label: 'Santé flotte', icon: Activity },
  { id: 'synthese_hebdo', label: 'Synthèse hebdo', icon: FileSpreadsheet },
  { id: 'synthese', label: 'Synthèse annuelle', icon: BarChart3 }
];

function ReportsView({ onNavigate }) {
  const { t, fmt } = useFormat();
  // Rapports = documents exportés (bordereaux) : devise de périmètre, jamais EUR en dur.
  const { moneyScope } = useOrgCurrency();
  const { user } = useAuth();
  const { data: vehiclesRaw = [] } = useVehicles();
  const { data: driversRaw = [] } = useDrivers();
  const { data: maintenanceRaw = [] } = useMaintenance();
  const { data: claimsRaw = [] } = useClaims();
  const { data: insuranceRaw = [] } = useInsurance();
  const vehicles = Array.isArray(vehiclesRaw) ? vehiclesRaw : [];
  const drivers = Array.isArray(driversRaw) ? driversRaw : [];
  const maintenance = Array.isArray(maintenanceRaw) ? maintenanceRaw : [];
  const claims = Array.isArray(claimsRaw) ? claimsRaw : [];
  const contracts = Array.isArray(insuranceRaw) ? insuranceRaw : [];

  const tabs = useMemo(() => TABS.map(tab => ({ ...tab, label: t(`reports.tabs.${tab.id}`, { defaultValue: tab.label }) })), [t]);
  const { visibleTabs, isReadOnly } = useTabPermissions('reports', tabs);
  const [activeTab, setActiveTab] = useState('conformite');

  // Reset active tab if it's not visible
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.find(t => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportConfig, setExportConfig] = useState({ scope: 'fleet', period: 'month', format: 'csv' });

  // Backend report state
  const [generatedReportId, setGeneratedReportId] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Sinistralite selectors
  const [selectedContractId, setSelectedContractId] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('');

  // Share and scheduling state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [showScheduleWizard, setShowScheduleWizard] = useState(false);

  // Email config state
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [emailConfig, setEmailConfig] = useState({
    senderName: '',
    senderEmail: '',
    recipients: ''
  });

  // Open email config modal before generating EML
  const handleEmailClick = async (report) => {
    // Set defaults from user context
    const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
    const userEmail = user?.email || '';

    // Show modal immediately with user defaults
    setEmailConfig({
      senderName: userName || 'FleetManager',
      senderEmail: userEmail || 'noreply@fleetmanager.local',
      recipients: ''
    });
    setShowEmailConfig(true);
    setLoadingRecipients(true);

    // Try to load subscribers for this report type
    try {
      const response = await reportSubscriptionAPI.list({ report_type: report.id || report.report_type });
      if (response.success && response.data?.length > 0) {
        // Get recipients from first active subscription
        const activeSubscription = response.data.find(s => s.is_enabled) || response.data[0];
        if (activeSubscription?.recipients_json?.length > 0) {
          const defaultRecipients = activeSubscription.recipients_json
            .map(r => r.email || r)
            .filter(Boolean)
            .join(', ');
          setEmailConfig(prev => ({ ...prev, recipients: defaultRecipients }));
        }
      }
    } catch (err) {
      console.warn('Could not load subscribers:', err);
    } finally {
      setLoadingRecipients(false);
    }
  };

  // Generate email (.eml) with PDF and CSV attachments via backend
  // Calls backend /reports/:id/eml endpoint which creates multipart MIME
  const [generatingEML, setGeneratingEML] = useState(false);

  const generateReportEmail = async (report) => {
    // Need backend report ID to get attachments
    if (!generatedReportId) {
      alert(t('reports.email.generateFirst'));
      return;
    }

    setGeneratingEML(true);
    try {
      await reportAPI.downloadEML(generatedReportId, {
        senderName: emailConfig.senderName,
        senderEmail: emailConfig.senderEmail,
        recipients: emailConfig.recipients
      });
      setShowEmailConfig(false);
    } catch (err) {
      console.error('EML generation error:', err);
      alert(t('reports.email.generateError', { error: err.message }));
    } finally {
      setGeneratingEML(false);
    }
  };

  // Calculate metrics with semantic status
  const insights = useMemo(() => {
    const today = new Date();

    // Compliance metrics
    const expiredLicenses = drivers.filter(d => {
      if (!d.license_expiry) return false;
      return new Date(d.license_expiry) <= today;
    }).length;

    const expiringLicenses = drivers.filter(d => {
      if (!d.license_expiry) return false;
      const expiry = new Date(d.license_expiry);
      const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      return daysLeft > 0 && daysLeft <= 30;
    }).length;

    const unassignedVehicles = vehicles.filter(v => !v.assigned_driver_id && v.status === 'available').length;

    const complianceStatus = expiredLicenses > 0 ? 'critical' : expiringLicenses > 0 ? 'warning' : 'ok';
    const complianceLabel = expiredLicenses > 0 ? t('reports.insights.expired', { count: expiredLicenses }) :
                           expiringLicenses > 0 ? t('reports.insights.toRenew', { count: expiringLicenses }) : t('reports.insights.compliant');

    // Cost metrics
    const maintenanceCosts = maintenance.reduce((sum, m) => sum + (m.total_cost || m.estimated_cost || 0), 0);
    const budget = 50000;
    const budgetUsage = Math.round((maintenanceCosts / budget) * 100);
    const costStatus = budgetUsage > 100 ? 'critical' : budgetUsage > 80 ? 'warning' : 'ok';
    const costLabel = budgetUsage > 100 ? t('reports.insights.budgetExceeded') :
                     budgetUsage > 80 ? t('reports.insights.percentUsed', { percent: budgetUsage }) : t('reports.insights.underControl');

    // Risk metrics
    const overdueClaimsCount = claims.filter(c => {
      if (CLOSED_CLAIM_STATUSES.includes(c.status)) return false;
      if (!c.event_date) return false;
      const eventDate = new Date(c.event_date);
      const daysSince = Math.ceil((today - eventDate) / (1000 * 60 * 60 * 24));
      return daysSince > 30;
    }).length;

    const openClaims = claims.filter(c => !CLOSED_CLAIM_STATUSES.includes(c.status)).length;
    const vehiclesInMaintenance = vehicles.filter(v => v.status === 'maintenance').length;

    const riskStatus = overdueClaimsCount > 0 ? 'critical' : openClaims > 3 ? 'warning' : 'ok';
    const riskLabel = overdueClaimsCount > 0 ? t('reports.insights.overdueClaims', { count: overdueClaimsCount }) :
                     openClaims > 3 ? t('reports.insights.openClaims', { count: openClaims }) : t('reports.insights.stable');

    return {
      conformite: {
        status: complianceStatus,
        label: complianceLabel,
        metrics: [
          { label: t('reports.insights.metrics.expiredLicenses'), value: expiredLicenses, highlight: expiredLicenses > 0 },
          { label: t('reports.insights.metrics.toRenew30'), value: expiringLicenses, highlight: expiringLicenses > 0 },
          { label: t('reports.insights.metrics.unassignedVehicles'), value: unassignedVehicles }
        ]
      },
      couts: {
        status: costStatus,
        label: costLabel,
        metrics: [
          { label: t('reports.insights.metrics.maintenanceCosts'), value: fmt.money(maintenanceCosts, 'EUR', { decimals: 0 }) },
          { label: t('reports.insights.metrics.budgetUsed'), value: `${budgetUsage}%`, highlight: budgetUsage > 80 },
          { label: t('reports.insights.metrics.interventions'), value: maintenance.length }
        ]
      },
      risques: {
        status: riskStatus,
        label: riskLabel,
        metrics: [
          { label: t('reports.insights.metrics.overdueClaims'), value: overdueClaimsCount, highlight: overdueClaimsCount > 0 },
          { label: t('reports.insights.metrics.openClaims'), value: openClaims },
          { label: t('reports.insights.metrics.immobilizedVehicles'), value: vehiclesInMaintenance, highlight: vehiclesInMaintenance > 0 }
        ]
      }
    };
  }, [vehicles, drivers, maintenance, claims, t, fmt]);

  // Report definitions grouped by tab — libellés : reports.catalog.<id>.* ; les colonnes de getData() sont le CONTENU du CSV de repli (FR, comme le backend)
  const reportsByTab = useMemo(() => ({
    conformite: [
      {
        id: 'vehicle_inventory',
        name: t('reports.catalog.vehicle_inventory.name'),
        description: t('reports.catalog.vehicle_inventory.description'),
        summary: t('reports.catalog.vehicle_inventory.summary', { returnObjects: true }),
        audience: t('reports.catalog.vehicle_inventory.audience'),
        dataScope: t('reports.dataScope.realtime'),
        buttonLabel: t('reports.catalog.vehicle_inventory.buttonLabel'),
        count: t('reports.counts.vehicles', { count: vehicles.length }),
        getData: () => vehicles.map(v => ({
          Immatriculation: v.license_plate, Marque: v.make, Modèle: v.model,
          Année: v.year, Statut: v.status, Kilométrage: v.mileage
        }))
      },
      {
        id: 'license_validity',
        name: t('reports.catalog.license_validity.name'),
        description: t('reports.catalog.license_validity.description'),
        summary: t('reports.catalog.license_validity.summary', { returnObjects: true }),
        audience: t('reports.catalog.license_validity.audience'),
        dataScope: t('reports.dataScope.realtime'),
        buttonLabel: t('reports.catalog.license_validity.buttonLabel'),
        count: t('reports.counts.drivers', { count: drivers.length }),
        getData: () => drivers.map(d => {
          const expiryDate = d.license_expiry ? new Date(d.license_expiry) : null;
          const daysUntilExpiry = expiryDate ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
          return {
            Conducteur: `${d.first_name} ${d.last_name}`, 'N° Permis': d.license_number,
            'Date expiration': d.license_expiry || 'Non renseigné', 'Jours restants': daysUntilExpiry || 'N/A',
            Alerte: daysUntilExpiry && daysUntilExpiry < 30 ? 'URGENT' : daysUntilExpiry && daysUntilExpiry < 90 ? 'À surveiller' : 'OK'
          };
        }).sort((a, b) => (a['Jours restants'] || 999) - (b['Jours restants'] || 999))
      },
      {
        id: 'assignment_history',
        name: t('reports.catalog.assignment_history.name'),
        description: t('reports.catalog.assignment_history.description'),
        summary: t('reports.catalog.assignment_history.summary', { returnObjects: true }),
        audience: t('reports.catalog.assignment_history.audience'),
        dataScope: t('reports.dataScope.realtime'),
        buttonLabel: t('reports.catalog.assignment_history.buttonLabel'),
        count: t('reports.counts.assignments', { count: vehicles.filter(v => v.assigned_driver_id).length }),
        getData: () => vehicles.filter(v => v.assigned_driver_id).map(v => {
          const driver = drivers.find(d => d.id === v.assigned_driver_id);
          return { Véhicule: `${v.make} ${v.model}`, Immatriculation: v.license_plate, Conducteur: driver ? `${driver.first_name} ${driver.last_name}` : 'Non assigné', Statut: v.status };
        })
      },
    ],
    couts: [
      {
        id: 'fleet_insurance',
        name: t('reports.catalog.fleet_insurance.name'),
        description: t('reports.catalog.fleet_insurance.description'),
        summary: t('reports.catalog.fleet_insurance.summary', { returnObjects: true }),
        audience: t('reports.catalog.fleet_insurance.audience'),
        dataScope: t('reports.dataScope.activeContracts'),
        buttonLabel: t('common.actions.export'),
        count: 'Excel / PDF',
        isDirectExport: true,
        getData: () => []
      },
      {
        id: 'tco',
        name: t('reports.catalog.tco.name'),
        description: t('reports.catalog.tco.description'),
        summary: t('reports.catalog.tco.summary', { returnObjects: true }),
        audience: t('reports.catalog.tco.audience'),
        dataScope: t('reports.dataScope.ytd'),
        buttonLabel: t('reports.catalog.tco.buttonLabel'),
        count: t('reports.counts.vehicles', { count: vehicles.length }),
        getData: () => vehicles.map(v => {
          const vehicleMaintenance = maintenance.filter(m => m.vehicle_id === v.id);
          const totalCost = vehicleMaintenance.reduce((sum, m) => sum + (m.total_cost || m.estimated_cost || 0), 0);
          return { Véhicule: `${v.make} ${v.model}`, Immatriculation: v.license_plate, 'Coût maintenance': moneyScope(totalCost), 'Nb interventions': vehicleMaintenance.length };
        }).sort((a, b) => parseInt(b['Coût maintenance'].replace(/\D/g, '')) - parseInt(a['Coût maintenance'].replace(/\D/g, '')))
      },
      {
        id: 'fuel_expenses',
        name: t('reports.catalog.fuel_expenses.name'),
        description: t('reports.catalog.fuel_expenses.description'),
        summary: t('reports.catalog.fuel_expenses.summary', { returnObjects: true }),
        audience: t('reports.catalog.fuel_expenses.audience'),
        dataScope: t('reports.dataScope.realtime'),
        buttonLabel: t('reports.catalog.fuel_expenses.buttonLabel'),
        count: t('reports.counts.vehicles', { count: vehicles.length }),
        getData: () => [{
          'Total véhicules': vehicles.length,
          'Véhicules diesel': vehicles.filter(v => v.fuel_type === 'diesel').length,
          'Véhicules essence': vehicles.filter(v => v.fuel_type === 'essence').length,
          'Véhicules électriques': vehicles.filter(v => v.fuel_type === 'electric').length,
          'Véhicules hybrides': vehicles.filter(v => v.fuel_type === 'hybrid').length
        }]
      },
      {
        id: 'budget_comparison',
        name: t('reports.catalog.budget_comparison.name'),
        description: t('reports.catalog.budget_comparison.description'),
        summary: t('reports.catalog.budget_comparison.summary', { returnObjects: true }),
        audience: t('reports.catalog.budget_comparison.audience'),
        dataScope: t('reports.dataScope.ytd'),
        buttonLabel: t('reports.catalog.budget_comparison.buttonLabel'),
        count: t('reports.counts.analysis'),
        getData: () => {
          const totalCost = maintenance.reduce((sum, m) => sum + (m.total_cost || m.estimated_cost || 0), 0);
          return [{ 'Budget maintenance': moneyScope(50000), 'Réalisé': moneyScope(totalCost), 'Écart': moneyScope(50000 - totalCost), 'Taux consommation': `${Math.round((totalCost / 50000) * 100)}%` }];
        }
      }
    ],
    synthese_hebdo: [
      {
        id: 'weekly_fleet_summary',
        name: t('reports.catalog.weekly_fleet_summary.name'),
        description: t('reports.catalog.weekly_fleet_summary.description'),
        summary: t('reports.catalog.weekly_fleet_summary.summary', { returnObjects: true }),
        audience: t('reports.catalog.weekly_fleet_summary.audience'),
        dataScope: t('reports.dataScope.weekVsPrevious'),
        buttonLabel: t('reports.catalog.weekly_fleet_summary.buttonLabel'),
        count: t('reports.counts.weekly'),
        getData: () => {
          const today = new Date();
          return [
            { Section: 'FLOTTE', Indicateur: 'Véhicules actifs', Valeur: vehicles.filter(v => v.status === 'active' || v.status === 'available').length },
            { Section: 'FLOTTE', Indicateur: 'Taux disponibilité', Valeur: `${vehicles.length > 0 ? Math.round(vehicles.filter(v => v.status === 'active' || v.status === 'available').length / vehicles.length * 100) : 0}%` },
            { Section: 'CONDUCTEURS', Indicateur: 'Total', Valeur: drivers.length },
            { Section: 'MAINTENANCE', Indicateur: 'Interventions', Valeur: maintenance.length },
            { Section: 'SINISTRES', Indicateur: 'Ouverts', Valeur: claims.filter(c => !CLOSED_CLAIM_STATUSES.includes(c.status)).length }
          ];
        }
      }
    ],
    risques: [
      {
        id: 'claims_status',
        name: t('reports.catalog.claims_status.name'),
        description: t('reports.catalog.claims_status.description'),
        summary: t('reports.catalog.claims_status.summary', { returnObjects: true }),
        audience: t('reports.catalog.claims_status.audience'),
        dataScope: t('reports.dataScope.realtime'),
        buttonLabel: t('reports.catalog.claims_status.buttonLabel'),
        count: t('reports.counts.files', { count: claims.filter(c => !CLOSED_CLAIM_STATUSES.includes(c.status)).length }),
        getData: () => claims.filter(c => !CLOSED_CLAIM_STATUSES.includes(c.status)).map(c => {
          const vehicle = vehicles.find(v => v.id === c.vehicle_id);
          const daysSince = c.event_date ? Math.ceil((new Date() - new Date(c.event_date)) / (1000 * 60 * 60 * 24)) : 0;
          return { 'Date incident': c.event_date, Véhicule: vehicle ? `${vehicle.make} ${vehicle.model}` : 'N/A', Immatriculation: vehicle?.license_plate || 'N/A', Type: c.type, Statut: c.status, 'Jours ouverts': daysSince, Urgence: daysSince > 30 ? 'RETARD' : daysSince > 14 ? 'À suivre' : 'Normal' };
        }).sort((a, b) => b['Jours ouverts'] - a['Jours ouverts'])
      },
      {
        id: 'downtime',
        name: t('reports.catalog.downtime.name'),
        description: t('reports.catalog.downtime.description'),
        summary: t('reports.catalog.downtime.summary', { returnObjects: true }),
        audience: t('reports.catalog.downtime.audience'),
        dataScope: t('reports.dataScope.realtime'),
        buttonLabel: t('reports.catalog.downtime.buttonLabel'),
        count: t('reports.counts.vehicles', { count: vehicles.filter(v => v.status === 'maintenance').length }),
        getData: () => vehicles.filter(v => v.status === 'maintenance').map(v => ({ Véhicule: `${v.make} ${v.model}`, Immatriculation: v.license_plate, Statut: 'En maintenance', Impact: 'Véhicule indisponible' }))
      },
      {
        id: 'breakdown_analysis',
        name: t('reports.catalog.breakdown_analysis.name'),
        description: t('reports.catalog.breakdown_analysis.description'),
        summary: t('reports.catalog.breakdown_analysis.summary', { returnObjects: true }),
        audience: t('reports.catalog.breakdown_analysis.audience'),
        dataScope: t('reports.dataScope.ytd'),
        buttonLabel: t('reports.catalog.breakdown_analysis.buttonLabel'),
        count: t('reports.counts.interventions', { count: maintenance.length }),
        getData: () => {
          const types = {};
          maintenance.forEach(m => { types[m.type || 'Non classifié'] = (types[m.type || 'Non classifié'] || 0) + 1; });
          return Object.entries(types).map(([type, count]) => ({ 'Type intervention': type, 'Nombre': count, 'Pourcentage': `${Math.round((count / maintenance.length) * 100)}%` })).sort((a, b) => b.Nombre - a.Nombre);
        }
      },
      {
        id: 'claims_sinistralite_contract',
        name: t('reports.catalog.claims_sinistralite_contract.name'),
        description: t('reports.catalog.claims_sinistralite_contract.description'),
        summary: t('reports.catalog.claims_sinistralite_contract.summary', { returnObjects: true }),
        audience: t('reports.catalog.claims_sinistralite_contract.audience'),
        dataScope: t('reports.dataScope.selectedContract'),
        buttonLabel: t('reports.catalog.claims_sinistralite_contract.buttonLabel'),
        count: t('reports.counts.activeContracts', { count: contracts.filter(c => c.status === 'active').length }),
        requiresFilter: true,
        filterComponent: 'contract_selector',
        getFilters: () => selectedContractId ? { contract_id: selectedContractId } : null,
        getData: () => []
      },
      {
        id: 'claims_sinistralite_driver',
        name: t('reports.catalog.claims_sinistralite_driver.name'),
        description: t('reports.catalog.claims_sinistralite_driver.description'),
        summary: t('reports.catalog.claims_sinistralite_driver.summary', { returnObjects: true }),
        audience: t('reports.catalog.claims_sinistralite_driver.audience'),
        dataScope: t('reports.dataScope.sinceHire'),
        buttonLabel: t('reports.catalog.claims_sinistralite_driver.buttonLabel'),
        count: t('reports.counts.activeDrivers', { count: drivers.filter(d => d.status === 'active').length }),
        requiresFilter: true,
        filterComponent: 'driver_selector',
        getFilters: () => selectedDriverId ? { driver_id: selectedDriverId } : null,
        getData: () => []
      },
      {
        id: 'attestation_sinistralite',
        name: t('reports.catalog.attestation_sinistralite.name'),
        description: t('reports.catalog.attestation_sinistralite.description'),
        summary: t('reports.catalog.attestation_sinistralite.summary', { returnObjects: true }),
        audience: t('reports.catalog.attestation_sinistralite.audience'),
        dataScope: t('reports.dataScope.sinceCoverageStart'),
        buttonLabel: t('reports.catalog.attestation_sinistralite.buttonLabel'),
        count: t('reports.counts.activeDrivers', { count: drivers.filter(d => d.status === 'active').length }),
        requiresFilter: true,
        filterComponent: 'driver_selector',
        getFilters: () => selectedDriverId ? { driver_id: selectedDriverId } : null,
        getData: () => []
      }
    ]
  }), [vehicles, drivers, maintenance, claims, contracts, selectedContractId, selectedDriverId, t]);

  // Direct export handler for fleet insurance (Excel or PDF)
  const handleFleetInsuranceExport = async (format = 'excel') => {
    try {
      setGenerating(true);
      const year = new Date().getFullYear();
      if (format === 'pdf') {
        await fleetInsuranceExportAPI.downloadPDF(year);
      } else {
        await fleetInsuranceExportAPI.download(year);
      }
    } catch (err) {
      console.error('Fleet insurance export error:', err);
      alert(t('reports.errors.download', { error: err.message }));
    } finally {
      setGenerating(false);
    }
  };

  // Handle report click
  const handleReportClick = (report) => {
    setSelectedReport(report);
    setGeneratedData(null);
    setGeneratedReportId(null);
  };

  // Helper functions for date ranges
  const getMonthStart = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
  const getMonthEnd = () => new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString();

  // Generate report via backend API
  const handleGenerateReport = async () => {
    if (!selectedReport) return;

    // Check if report requires a filter (e.g. contract or driver selection)
    if (selectedReport.requiresFilter && selectedReport.getFilters) {
      const filters = selectedReport.getFilters();
      if (!filters) {
        alert(t('reports.errors.selectFirst'));
        return;
      }
    }

    setGenerating(true);
    setGeneratedReportId(null);

    try {
      const reportFilters = selectedReport.getFilters ? selectedReport.getFilters() : {};
      const response = await reportAPI.generate(selectedReport.id, {
        format: 'csv',
        periodStart: exportConfig.period === 'month' ? getMonthStart() : undefined,
        periodEnd: exportConfig.period === 'month' ? getMonthEnd() : undefined,
        filters: reportFilters || {},
      });

      if (response.ok && response.data) {
        setGeneratedReportId(response.data.id);
        const data = selectedReport.getData();
        setGeneratedData(data);
      } else {
        console.warn('Backend report generation failed, using client-side fallback');
        const data = selectedReport.getData();
        setGeneratedData(data);
      }
    } catch (err) {
      console.error('Report generation error:', err);
      const data = selectedReport.getData();
      setGeneratedData(data);
    } finally {
      setGenerating(false);
    }
  };

  // Download report from backend
  const handleDownloadReport = async (reportId, format = 'csv') => {
    setDownloadingReport(true);
    try {
      await reportAPI.download(reportId, format);
    } catch (err) {
      console.error('Download error:', err);
      alert(t('reports.errors.download', { error: err.message }));
    } finally {
      setDownloadingReport(false);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (generatedReportId) {
      handleDownloadReport(generatedReportId, 'csv');
      return;
    }
    if (!generatedData || generatedData.length === 0) return;
    const headers = Object.keys(generatedData[0]);
    const csvContent = [
      headers.join(';'),
      ...generatedData.map(row => headers.map(h => row[h] || '').join(';'))
    ].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${selectedReport.id}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Export to PDF
  const handleExportPDF = async () => {
    if (!generatedReportId) {
      alert(t('reports.errors.generateFirstPdf'));
      return;
    }
    setDownloadingPDF(true);
    try {
      await reportAPI.download(generatedReportId, 'pdf');
    } catch (err) {
      console.error('PDF download error:', err);
      alert(t('reports.errors.downloadPdf', { error: err.message }));
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Generate monthly report
  const handleGenerateMonthlyReport = async () => {
    const monthlyData = {
      generated: new Date().toISOString(),
      period: 'Ce mois',
      fleet: { total: vehicles.length, active: vehicles.filter(v => v.status === 'active' || v.status === 'available').length },
      drivers: { total: drivers.length, licensesExpiring: insights.conformite.metrics[1].value },
      maintenance: { interventions: maintenance.length, costs: maintenance.reduce((sum, m) => sum + (m.total_cost || m.estimated_cost || 0), 0) },
      claims: { total: claims.length, open: claims.filter(c => !CLOSED_CLAIM_STATUSES.includes(c.status)).length }
    };

    const reportData = [
      { 'Section': 'FLOTTE', 'Indicateur': 'Véhicules totaux', 'Valeur': monthlyData.fleet.total },
      { 'Section': 'FLOTTE', 'Indicateur': 'Véhicules actifs', 'Valeur': monthlyData.fleet.active },
      { 'Section': 'CONDUCTEURS', 'Indicateur': 'Total conducteurs', 'Valeur': monthlyData.drivers.total },
      { 'Section': 'CONDUCTEURS', 'Indicateur': 'Permis à renouveler', 'Valeur': monthlyData.drivers.licensesExpiring },
      { 'Section': 'MAINTENANCE', 'Indicateur': 'Interventions', 'Valeur': monthlyData.maintenance.interventions },
      { 'Section': 'MAINTENANCE', 'Indicateur': 'Coûts totaux', 'Valeur': moneyScope(monthlyData.maintenance.costs) },
      { 'Section': 'SINISTRES', 'Indicateur': 'Total sinistres', 'Valeur': monthlyData.claims.total },
      { 'Section': 'SINISTRES', 'Indicateur': 'Sinistres ouverts', 'Valeur': monthlyData.claims.open }
    ];

    setSelectedReport({
      id: 'monthly_report', name: t('reports.catalog.monthly_report.name'),
      summary: t('reports.catalog.monthly_report.summary', { returnObjects: true }),
      audience: t('reports.catalog.monthly_report.audience'),
      dataScope: t('reports.dataScope.realtime'),
      buttonLabel: t('reports.catalog.monthly_report.buttonLabel'),
      getData: () => reportData
    });
    setGeneratedData(reportData);
  };

  const closeModal = () => {
    setSelectedReport(null);
    setGeneratedData(null);
  };

  // Get status indicator color
  const getStatusColor = (status) => {
    switch (status) {
      case 'critical': return 'bg-red-500';
      case 'warning': return 'bg-amber-500';
      default: return 'bg-emerald-500';
    }
  };

  const currentTabInsights = insights[activeTab];
  const currentReports = reportsByTab[activeTab] || [];

  return (
    <PageTemplate
      icon={BarChart3}
      iconColor="text-blue-600"
      title={t('reports.title')}
      subtitle={t('reports.subtitle')}
      accentColor="blue"
      tabs={visibleTabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      actions={
        <ReadOnlyGuard moduleId="reports">
          <div className="flex gap-2">
            <button
              onClick={() => setShowScheduleWizard(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-md"
            >
              <Calendar className="w-4 h-4" />
              {t('reports.actions.schedule')}
            </button>
            <button
              onClick={handleGenerateMonthlyReport}
              className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors flex items-center gap-2 shadow-md"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {t('reports.catalog.monthly_report.name')}
            </button>
          </div>
        </ReadOnlyGuard>
      }
    >
      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">

        {/* Tab Content */}
        <div className="p-6">
          {/* Annual Summary Tab - Separate content */}
          {activeTab === 'synthese' ? (
            <AnnualSummaryTab />
          ) : activeTab === 'sante_flotte' ? (
            <FleetHealthHeatmap />
          ) : (
            <>
              {/* Contextual Summary - Subtle */}
              <div className="flex items-center gap-6 mb-6 pb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    {activeTab === 'conformite' && <Shield className="w-5 h-5 text-slate-600" />}
                    {activeTab === 'couts' && <Euro className="w-5 h-5 text-slate-600" />}
                    {activeTab === 'risques' && <AlertTriangle className="w-5 h-5 text-slate-600" />}
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">{t('reports.insights.globalStatus')}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-slate-800">{currentTabInsights?.label}</span>
                      <span className={`w-2 h-2 rounded-full ${getStatusColor(currentTabInsights?.status)}`} />
                    </div>
                  </div>
                </div>
                <div className="h-8 w-px bg-slate-200" />
                {currentTabInsights?.metrics.map((metric, idx) => (
                  <div key={idx}>
                    <div className="text-xs text-slate-500">{metric.label}</div>
                    <div className={`text-lg font-semibold ${metric.highlight ? 'text-amber-600' : 'text-slate-800'}`}>
                      {metric.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Reports List */}
              <div className="space-y-2">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
                  {t('reports.list.available')}
                </div>

                {currentReports.map((report) => (
                  <div
                    key={report.id}
                    className="w-full p-4 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => handleReportClick(report)}
                        className="flex items-center gap-4 flex-1 text-left"
                      >
                        <div className="w-10 h-10 bg-white rounded-lg border border-slate-200 flex items-center justify-center group-hover:border-slate-300">
                          <FileText className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-800 group-hover:text-slate-900">
                            {report.name}
                          </div>
                          <div className="text-sm text-slate-500">
                            {report.description}
                          </div>
                        </div>
                      </button>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-400">{report.count}</span>
                        {!isReadOnly && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShareTarget({ type: 'report', id: report.id, title: report.name });
                              setShowShareDialog(true);
                            }}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title={t('reports.actions.share')}
                          >
                            <Share2 className="w-4 h-4" />
                          </button>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
                      </div>
                    </div>

                    {/* Inline filter selector for sinistralite reports */}
                    {report.filterComponent === 'contract_selector' && (
                      <div className="mt-3 ml-14">
                        <select
                          value={selectedContractId}
                          onChange={(e) => setSelectedContractId(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full max-w-md"
                        >
                          <option value="">{t('reports.selectors.contract')}</option>
                          {contracts.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.insurer_name || t('reports.selectors.insurer')} — {c.policy_number || t('reports.selectors.contractNumber', { id: c.id })} ({c.status === 'active' ? t('reports.selectors.active') : c.status})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {report.filterComponent === 'driver_selector' && (
                      <div className="mt-3 ml-14">
                        <select
                          value={selectedDriverId}
                          onChange={(e) => setSelectedDriverId(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full max-w-md"
                        >
                          <option value="">{t('reports.selectors.driver')}</option>
                          {drivers.filter(d => d.status === 'active').map(d => (
                            <option key={d.id} value={d.id}>
                              {d.first_name} {(d.last_name || '').toUpperCase()}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals - placed inside PageTemplate children */}
      <ReportModal
        report={selectedReport}
        generatedData={generatedData}
        generating={generating}
        generatedReportId={generatedReportId}
        downloadingReport={downloadingReport}
        downloadingPDF={downloadingPDF}
        onClose={closeModal}
        onGenerate={handleGenerateReport}
        onExportCSV={handleExportCSV}
        onExportPDF={handleExportPDF}
        onEmail={() => selectedReport && handleEmailClick(selectedReport)}
        onRegenerate={() => setGeneratedData(null)}
        onDirectExport={handleFleetInsuranceExport}
      />

      <ExportModal
        show={showExportModal}
        config={exportConfig}
        onConfigChange={setExportConfig}
        onClose={() => setShowExportModal(false)}
        onExport={() => { handleGenerateMonthlyReport(); setShowExportModal(false); }}
      />

      {/* Share Dialog */}
      <ShareDialog
        isOpen={showShareDialog}
        onClose={() => {
          setShowShareDialog(false);
          setShareTarget(null);
        }}
        shareType={shareTarget?.type || 'report'}
        scope={{ reportType: shareTarget?.id }}
        title={shareTarget?.title}
        onSuccess={(result) => {
          console.log('Share created:', result);
        }}
      />

      {/* Report Schedule Wizard */}
      <ReportScheduleWizard
        isOpen={showScheduleWizard}
        onClose={() => setShowScheduleWizard(false)}
        onSuccess={(subscription) => {
          console.log('Subscription created:', subscription);
          setShowScheduleWizard(false);
        }}
      />

      {/* Email Config Modal */}
      {showEmailConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowEmailConfig(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-slate-800">{t('reports.email.title')}</h2>
              </div>
              <button onClick={() => setShowEmailConfig(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Sender */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('reports.email.senderName')}</label>
                  <input
                    type="text"
                    value={emailConfig.senderName}
                    onChange={(e) => setEmailConfig({ ...emailConfig, senderName: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('reports.email.senderEmail')}</label>
                  <input
                    type="email"
                    value={emailConfig.senderEmail}
                    onChange={(e) => setEmailConfig({ ...emailConfig, senderEmail: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Recipients */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-slate-700">
                    {t('reports.email.recipients')} <span className="text-slate-400 font-normal">{t('reports.email.commaSeparated')}</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => { setShowEmailConfig(false); setShowScheduleWizard(true); }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {t('reports.email.manageSubscriptions')}
                  </button>
                </div>
                <div className="relative">
                  <textarea
                    value={emailConfig.recipients}
                    onChange={(e) => setEmailConfig({ ...emailConfig, recipients: e.target.value })}
                    placeholder={loadingRecipients ? t('reports.email.loadingSubscribers') : "client1@example.com, client2@example.com"}
                    rows={2}
                    disabled={loadingRecipients}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100"
                  />
                  {loadingRecipients && (
                    <div className="absolute right-3 top-3">
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                {emailConfig.recipients && (
                  <p className="mt-1 text-xs text-green-600">
                    {t('reports.email.recipientsFromSubscriptions', { count: emailConfig.recipients.split(',').filter(e => e.trim()).length })}
                  </p>
                )}
              </div>

              {/* Info */}
              {!generatedReportId ? (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                  <strong>{t('reports.email.warningLabel')}</strong> {t('reports.email.warningText')}
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
                  <strong>{t('reports.email.attachmentsLabel')}</strong> {t('reports.email.attachmentsText')}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => setShowEmailConfig(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
              >
                {t('common.actions.cancel')}
              </button>
              <button
                onClick={() => selectedReport && generateReportEmail(selectedReport)}
                disabled={generatingEML || !generatedReportId}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {generatingEML ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('reports.email.generating')}
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    {t('reports.email.generateEml')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageTemplate>
  );
}

export default ReportsView;
